/**
 * Benefits Capture — live reads of the clinic-day docs the scheduled builder
 * writes (`organizations/{orgId}/benefitsCaptureDays`). The page subscribes
 * with onSnapshot under the rules (any member of the org, staff viewing), the
 * way the Cockpit reads appointment rows, and hands the docs to the pure
 * period fold (utils/benefitsCapturePeriod). No callable, no query cap.
 */
import {
  collection, doc, onSnapshot, query, where, orderBy, type Unsubscribe,
} from 'firebase/firestore'
import { db } from '../config'
import {
  REASON_KEYS,
  type BenefitsCaptureDayDoc, type BenefitsCaptureStatusDoc, type ReasonCounts, type StatusClinic,
} from '../utils/benefitsCapturePeriod'

const STATUS_DOC_ID = '_status'

function daysCollection(orgId: string) {
  return collection(db, 'organizations', orgId, 'benefitsCaptureDays')
}

const num = (v: unknown): number => (typeof v === 'number' && Number.isFinite(v) ? v : 0)

function toReasons(raw: unknown): ReasonCounts {
  const rec = (raw && typeof raw === 'object' ? raw : {}) as Record<string, unknown>
  const out = {} as ReasonCounts
  for (const k of REASON_KEYS) out[k] = num(rec[k])
  return out
}

function toDayDoc(data: Record<string, unknown>): BenefitsCaptureDayDoc | null {
  if (typeof data.day !== 'string' || typeof data.clinicId !== 'string') return null
  return {
    clinicId: data.clinicId,
    clinicName: typeof data.clinicName === 'string' ? data.clinicName : data.clinicId,
    clinicStatus: typeof data.clinicStatus === 'string' ? data.clinicStatus : 'active',
    day: data.day,
    visits: num(data.visits),
    medicalVerified: num(data.medicalVerified),
    activeCoverage: num(data.activeCoverage),
    visionVerified: num(data.visionVerified),
    visionOnly: num(data.visionOnly),
    noInsurance: num(data.noInsurance),
    reasons: toReasons(data.reasons),
    ...(data.truncated === true ? { truncated: true } : {}),
  }
}

function toStatusDoc(data: Record<string, unknown> | undefined): BenefitsCaptureStatusDoc {
  const clinics: StatusClinic[] = []
  if (Array.isArray(data?.clinics)) {
    for (const c of data.clinics as Array<Record<string, unknown>>) {
      if (!c || typeof c.clinicId !== 'string') continue
      clinics.push({
        clinicId: c.clinicId,
        name: typeof c.name === 'string' && c.name ? c.name : c.clinicId,
        status: typeof c.status === 'string' ? c.status : 'active',
        timezone: typeof c.timezone === 'string' ? c.timezone : '',
      })
    }
  }
  return { clinics, goLiveDay: typeof data?.goLiveDay === 'string' ? data.goLiveDay : null }
}

/**
 * The `_status` doc: the org's clinics and its go-live day. `null` until the
 * builder (or the staff switch) has written it — the page then has no clinic
 * rows to draw yet.
 */
export function subscribeBenefitsCaptureStatus(
  orgId: string,
  onData: (status: BenefitsCaptureStatusDoc | null) => void,
  onError: (err: Error) => void,
): Unsubscribe {
  return onSnapshot(
    doc(daysCollection(orgId), STATUS_DOC_ID),
    (snap) => onData(snap.exists() ? toStatusDoc(snap.data()) : null),
    onError,
  )
}

/**
 * Every clinic's day docs from `fromDay` (null = no lower bound) through
 * `toDay`, live. One range query on `day` — the `_status` doc carries no
 * `day` and never matches.
 */
export function subscribeBenefitsCaptureDays(
  orgId: string,
  fromDay: string | null,
  toDay: string,
  onData: (docs: BenefitsCaptureDayDoc[]) => void,
  onError: (err: Error) => void,
): Unsubscribe {
  const clauses = [
    ...(fromDay ? [where('day', '>=', fromDay)] : []),
    where('day', '<=', toDay),
    orderBy('day'),
  ]
  return onSnapshot(
    query(daysCollection(orgId), ...clauses),
    (snap) => {
      const docs: BenefitsCaptureDayDoc[] = []
      for (const d of snap.docs) {
        const parsed = toDayDoc(d.data() as Record<string, unknown>)
        if (parsed) docs.push(parsed)
      }
      onData(docs)
    },
    onError,
  )
}
